// Phase 57 — Customer Spreadsheet View & Editor.
//
// Configurable columns (visibility persisted to localStorage), inline cell
// edit (saves on blur/Enter via PUT /customers/{id}; list fields open a
// popover), bulk actions (delete / tag / export selection), filters,
// sort, pagination (50/page max). Also surfaces a Phase-56 duplicate
// badge per row that deep-links to the merge UI.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search, ChevronLeft, ChevronRight, Trash2, Tag as TagIcon, Download,
  Settings2, ArrowUpDown, AlertTriangle, X, Edit2,
} from "lucide-react";
import { TagChip } from "@/components/TagChip";

const COLUMN_DEFS = [
  { key: "company_name", label: "Company",   editable: true,  type: "text",  visible: true },
  { key: "contact_name", label: "Contact",   editable: true,  type: "text",  visible: true },
  { key: "phones",       label: "Phone",     editable: true,  type: "list",  visible: true },
  { key: "emails",       label: "Email",     editable: true,  type: "emails", visible: true },
  { key: "tags",         label: "Tags",      editable: false, type: "tags",  visible: false },
  { key: "notes",        label: "Notes",     editable: true,  type: "text",  visible: false },
  { key: "created_at",   label: "Created",   editable: false, type: "date",  visible: false },
];

const STORAGE_KEY = "jbk:customers-spreadsheet:cols";
const PAGE_LIMIT = 50;

function loadVisibleCols() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch { /* ignore */ }
  const out = {};
  COLUMN_DEFS.forEach((c) => { out[c.key] = c.visible; });
  return out;
}

function saveVisibleCols(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

export default function CustomersSpreadsheet({ showOneOff: showOneOffProp, onShowOneOffChange } = {}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");
  const [hasBookings, setHasBookings] = useState("all");
  const [archivedView, setArchivedView] = useState("active");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [colsVisible, setColsVisible] = useState(loadVisibleCols());
  const [allTags, setAllTags] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [duplicateClusters, setDuplicateClusters] = useState({});  // {customer_id: cluster_obj}
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [tagBulkPick, setTagBulkPick] = useState([]);

  useEffect(() => { saveVisibleCols(colsVisible); }, [colsVisible]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        paginated: true,
        page,
        limit: PAGE_LIMIT,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (q.trim()) params.q = q.trim();
      if (hasBookings !== "all") params.has_bookings = hasBookings === "yes";
      if (archivedView === "archived") params.include_archived = true;
      if (tagFilter !== "all") params.tag = tagFilter;
      // Phase 59.3 — opt-in to including hidden one-off (Private Cash)
      // customers. Driven by a parent-supplied prop so the toggle stays
      // synced when the user flips between List and Spreadsheet tabs.
      if (showOneOffProp) params.include_one_off = true;
      const r = await api.get("/customers", { params });
      let envelope = r.data;
      if (Array.isArray(envelope)) {
        envelope = { items: envelope, total: envelope.length, page: 1, total_pages: 1, limit: envelope.length };
      }
      let rows = envelope.items || [];
      if (archivedView === "archived") rows = rows.filter((c) => c.archived_at);
      setItems(rows);
      setTotal(envelope.total || rows.length);
      setTotalPages(envelope.total_pages || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const r = await api.get("/customer-tags");
      setAllTags(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch { /* tags optional */ }
  };

  const loadDuplicates = async () => {
    try {
      const r = await api.get("/customers/duplicates");
      const map = {};
      for (const cl of (r.data?.clusters || [])) {
        for (const c of (cl.customers || [])) map[c.id] = cl;
      }
      setDuplicateClusters(map);
    } catch { /* duplicates badge is optional */ }
  };

  useEffect(() => { load(); }, [page, sortBy, sortDir, hasBookings, archivedView, tagFilter, showOneOffProp]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTags(); loadDuplicates(); }, []);

  const visibleCols = useMemo(() => COLUMN_DEFS.filter((c) => colsVisible[c.key]), [colsVisible]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(items.map((c) => c.id)));
    else setSelected(new Set());
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const updateCustomer = async (id, patch) => {
    try {
      await api.put(`/customers/${id}`, patch);
      toast.success("Saved");
      setItems((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selected);
      const r = await api.post("/customers/bulk-delete", { customer_ids: ids });
      toast.success(`Archived ${r.data?.archived || 0} customers`);
      setSelected(new Set());
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleBulkTag = async () => {
    try {
      const r = await api.post("/customers/bulk-tag", {
        customer_ids: Array.from(selected),
        tag_ids: tagBulkPick,
      });
      toast.success(`Tagged ${r.data?.tagged || 0} customers`);
      setSelected(new Set());
      setBulkTagOpen(false);
      setTagBulkPick([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleBulkExport = async () => {
    try {
      const r = await api.post(
        "/customers/export-selection",
        { customer_ids: Array.from(selected) },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "customers_selection.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.size} customers`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="customers-spreadsheet">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name/phone/email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
            className="pl-9 h-10"
            data-testid="spreadsheet-search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => { setPage(1); load(); }} data-testid="spreadsheet-search-go">Go</Button>

        <Select value={tagFilter} onValueChange={(v) => { setTagFilter(v); setPage(1); }}>
          <SelectTrigger className="h-10 w-[150px]" data-testid="spreadsheet-tag-filter">
            <SelectValue placeholder="Tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {allTags.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.label || t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={hasBookings} onValueChange={(v) => { setHasBookings(v); setPage(1); }}>
          <SelectTrigger className="h-10 w-[160px]" data-testid="spreadsheet-bookings-filter">
            <SelectValue placeholder="Bookings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Has bookings</SelectItem>
            <SelectItem value="no">No bookings</SelectItem>
          </SelectContent>
        </Select>

        <Select value={archivedView} onValueChange={(v) => { setArchivedView(v); setPage(1); }}>
          <SelectTrigger className="h-10 w-[150px]" data-testid="spreadsheet-archived-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        <button
          type="button"
          className={`mono text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-[6px] border transition-colors ${
            showOneOffProp
              ? "bg-violet-100 text-violet-700 border-violet-300"
              : "bg-secondary text-muted-foreground border-border hover:border-violet-300"
          }`}
          onClick={() => onShowOneOffChange?.(!showOneOffProp)}
          data-testid="spreadsheet-show-oneoff-toggle"
          title="Show hidden one-off (Private Cash) customers"
        >
          {showOneOffProp ? "✓ One-offs" : "Show one-offs"}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="spreadsheet-cols-menu">
              <Settings2 className="h-4 w-4" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {COLUMN_DEFS.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={!!colsVisible[c.key]}
                onCheckedChange={(v) => setColsVisible((m) => ({ ...m, [c.key]: !!v }))}
                data-testid={`spreadsheet-col-toggle-${c.key}`}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selected.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-[8px] px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="bulk-actions-bar">
          <span className="text-sm font-bold">{selected.size} selected</span>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBulkTagOpen(true)} data-testid="bulk-tag-btn">
            <TagIcon className="h-3.5 w-3.5" /> Tag
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={handleBulkExport} data-testid="bulk-export-btn">
            <Download className="h-3.5 w-3.5" /> Export
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-red-700 hover:bg-red-50" onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-btn">
            <Trash2 className="h-3.5 w-3.5" /> Archive
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="bg-card border border-border rounded-[8px] overflow-x-auto" data-testid="spreadsheet-table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="w-10 p-2">
                <Checkbox
                  checked={items.length > 0 && selected.size === items.length}
                  onCheckedChange={toggleAll}
                  data-testid="spreadsheet-select-all"
                />
              </th>
              <th className="w-6 p-2"></th>
              {visibleCols.map((c) => (
                <th key={c.key} className="text-left p-2">
                  <button
                    type="button"
                    onClick={() => handleSort(c.key)}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold hover:text-accent transition-colors"
                    data-testid={`spreadsheet-th-${c.key}`}
                  >
                    {c.label}
                    {sortBy === c.key ? (sortDir === "asc" ? "↑" : "↓") : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={visibleCols.length + 2} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={visibleCols.length + 2} className="p-6 text-center text-muted-foreground">No customers match.</td></tr>
            ) : items.map((c) => (
              <Row
                key={c.id}
                customer={c}
                visibleCols={visibleCols}
                selected={selected.has(c.id)}
                onToggle={(v) => toggleOne(c.id, v)}
                onUpdate={(patch) => updateCustomer(c.id, patch)}
                dupCluster={duplicateClusters[c.id]}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm" data-testid="spreadsheet-pagination">
        <div className="text-muted-foreground">
          Page {page} of {totalPages} · {total} customers
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {selected.size} customers?</AlertDialogTitle>
            <AlertDialogDescription>
              These customers will be soft-archived. They can be restored from the Find Duplicates page within 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete-ok">
              Yes, archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk tag dialog */}
      <AlertDialog open={bulkTagOpen} onOpenChange={setBulkTagOpen}>
        <AlertDialogContent data-testid="bulk-tag-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply tags to {selected.size} customers</AlertDialogTitle>
            <AlertDialogDescription>Tag picks are unioned onto each selected customer.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2 max-h-60 overflow-y-auto">
            {allTags.map((t) => {
              const checked = tagBulkPick.includes(t.id);
              return (
                <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => setTagBulkPick((prev) =>
                      v ? [...prev, t.id] : prev.filter((x) => x !== t.id),
                    )}
                    data-testid={`bulk-tag-option-${t.id}`}
                  />
                  <TagChip tag={t} size="sm" />
                </label>
              );
            })}
            {allTags.length === 0 && (
              <div className="text-xs text-muted-foreground italic">No tags yet — create tags from Settings.</div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-tag-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkTag}
              disabled={tagBulkPick.length === 0}
              data-testid="bulk-tag-ok"
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ customer, visibleCols, selected, onToggle, onUpdate, dupCluster }) {
  const c = customer;
  return (
    <tr className="border-t border-border hover:bg-muted/20" data-testid={`spreadsheet-row-${c.id}`}>
      <td className="p-2 align-top">
        <Checkbox checked={selected} onCheckedChange={onToggle} data-testid={`spreadsheet-select-${c.id}`} />
      </td>
      <td className="p-2 align-top w-6">
        {dupCluster ? (
          <Link
            to={`/customers/merge?ids=${encodeURIComponent(dupCluster.customers.map((x) => x.id).join(","))}`}
            title={`Possible duplicate · ${dupCluster.signals.join(", ")}`}
            data-testid={`spreadsheet-dup-badge-${c.id}`}
          >
            <AlertTriangle className="h-4 w-4 text-amber-600 hover:text-amber-700" />
          </Link>
        ) : c.is_one_off ? (
          <span
            className="mono text-[8px] uppercase tracking-widest font-bold bg-violet-100 text-violet-700 border border-violet-200 rounded-sm px-1 py-0"
            title="Private cash — one-off customer"
            data-testid={`spreadsheet-oneoff-badge-${c.id}`}
          >
            1-off
          </span>
        ) : null}
      </td>
      {visibleCols.map((col) => (
        <td key={col.key} className="p-2 align-top">
          <Cell customer={c} col={col} onUpdate={onUpdate} />
        </td>
      ))}
    </tr>
  );
}

function Cell({ customer, col, onUpdate }) {
  const value = customer[col.key];
  // Phase 59.1 — `company_name` is special: single-click MUST navigate to
  // the customer profile, not open inline edit. We render it as a <Link>
  // with a small explicit pencil affordance to invoke edit-mode.
  if (col.key === "company_name") {
    return <CompanyNameCell customer={customer} value={value || ""} onSave={(v) => onUpdate({ company_name: v })} />;
  }
  if (!col.editable) {
    if (col.type === "tags") {
      return (
        <div className="flex flex-wrap gap-1">
          {(customer.tags || []).map((t) => <TagChip key={t.id} tag={t} size="xs" />)}
        </div>
      );
    }
    if (col.type === "date") {
      try { return <span className="text-xs text-muted-foreground">{new Date(value).toLocaleDateString()}</span>; }
      catch { return value || ""; }
    }
    return <span>{value || ""}</span>;
  }
  if (col.type === "text") {
    return <InlineText value={value || ""} onSave={(v) => onUpdate({ [col.key]: v })} testid={`cell-${customer.id}-${col.key}`} />;
  }
  if (col.type === "list") {
    return <PhoneListPopover values={value || []} onSave={(v) => onUpdate({ [col.key]: v })} customerId={customer.id} />;
  }
  if (col.type === "emails") {
    return <EmailListPopover values={value || []} onSave={(v) => onUpdate({ [col.key]: v })} customerId={customer.id} />;
  }
  return <span>{value || ""}</span>;
}

function InlineText({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  const ref = useRef();
  useEffect(() => { setLocal(value); }, [value]);
  if (!editing) {
    return (
      <button
        type="button"
        className="text-left w-full hover:bg-muted/30 rounded px-1 py-0.5 transition-colors min-h-[24px]"
        onClick={() => setEditing(true)}
        data-testid={testid}
      >
        <span className={value ? "" : "text-muted-foreground italic"}>{value || "—"}</span>
      </button>
    );
  }
  return (
    <Input
      ref={ref}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { setEditing(false); if (local !== value) onSave(local); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.currentTarget.blur(); }
        if (e.key === "Escape") { setLocal(value); setEditing(false); }
      }}
      autoFocus
      className="h-8"
      data-testid={`${testid}-input`}
    />
  );
}

// Phase 59.1 — name cell that NAVIGATES on click, with an explicit pencil
// affordance for inline edit. Single-click on the name text = navigate;
// click the pencil (or double-click the name) = edit. Other columns keep
// their existing single-click-to-edit behaviour.
function CompanyNameCell({ customer, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  if (editing) {
    return (
      <Input
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); if (local !== value) onSave(local); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setLocal(value); setEditing(false); }
        }}
        autoFocus
        className="h-8"
        data-testid={`cell-${customer.id}-company_name-input`}
      />
    );
  }
  return (
    <div className="flex items-center gap-1 group/cn">
      <Link
        to={`/customers/${customer.id}`}
        className="flex-1 min-w-0 truncate hover:underline hover:text-accent transition-colors"
        onDoubleClick={(e) => { e.preventDefault(); setEditing(true); }}
        data-testid={`cell-${customer.id}-company_name`}
        title={`Open ${value || "customer"} profile (double-click to rename)`}
      >
        <span className={value ? "font-medium" : "text-muted-foreground italic"}>
          {value || "—"}
        </span>
      </Link>
      <button
        type="button"
        className="opacity-0 group-hover/cn:opacity-100 transition-opacity text-muted-foreground hover:text-accent rounded p-1"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={`cell-${customer.id}-company_name-edit`}
        title="Rename"
      >
        <Edit2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function PhoneListPopover({ values, onSave, customerId }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(values);
  useEffect(() => { setLocal(values); }, [open, values]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left w-full hover:bg-muted/30 rounded px-1 py-0.5 transition-colors text-xs"
          data-testid={`cell-${customerId}-phones`}
        >
          {(values || []).join(", ") || <span className="text-muted-foreground italic">—</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid={`phones-popover-${customerId}`}>
        <div className="space-y-2">
          {(local || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={p}
                onChange={(e) => setLocal((prev) => prev.map((x, j) => (i === j ? e.target.value : x)))}
                className="h-8"
                data-testid={`phones-edit-${customerId}-${i}`}
              />
              <Button size="sm" variant="ghost" onClick={() => setLocal((prev) => prev.filter((_, j) => j !== i))}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="w-full" onClick={() => setLocal((prev) => [...prev, ""])}>
            + Add phone
          </Button>
          <Button
            size="sm"
            className="w-full"
            onClick={() => { onSave(local.filter((p) => p.trim())); setOpen(false); }}
            data-testid={`phones-save-${customerId}`}
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EmailListPopover({ values, onSave, customerId }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(values);
  useEffect(() => { setLocal(values); }, [open, values]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left w-full hover:bg-muted/30 rounded px-1 py-0.5 transition-colors text-xs break-all"
          data-testid={`cell-${customerId}-emails`}
        >
          {(values || []).map((e) => e.email || e).join(", ") || <span className="text-muted-foreground italic">—</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96" data-testid={`emails-popover-${customerId}`}>
        <div className="space-y-2">
          {(local || []).map((e, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={e.email || ""}
                onChange={(ev) => setLocal((prev) => prev.map((x, j) => (i === j ? { ...x, email: ev.target.value } : x)))}
                className="h-8"
                data-testid={`emails-edit-${customerId}-${i}`}
              />
              <label className="flex items-center gap-1 text-[10px]">
                <Checkbox
                  checked={!!e.receives_invoices}
                  onCheckedChange={(v) => setLocal((prev) => prev.map((x, j) => (i === j ? { ...x, receives_invoices: !!v } : x)))}
                />
                Invoices
              </label>
              <Button size="sm" variant="ghost" onClick={() => setLocal((prev) => prev.filter((_, j) => j !== i))}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="w-full" onClick={() => setLocal((prev) => [...prev, { email: "", receives_invoices: false }])}>
            + Add email
          </Button>
          <Button
            size="sm"
            className="w-full"
            onClick={() => { onSave(local.filter((e) => (e.email || "").trim())); setOpen(false); }}
            data-testid={`emails-save-${customerId}`}
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
